import { cn, formatDateOnly, Icon } from '@dataroom/shared';
import type { FunnelStage, RoomFunnelResponse } from '@dataroom/shared';

// The full funnel ladder, in order. The buyer's `stage` is compared by position,
// so every step at or before the current stage reads as done/current even when
// the backend advances more than one rung at once (e.g. teaser → cim on seal).
// The dated post-LOI steps (exclusivity/closing, PRD-0004 §1.1) surface the date
// the admin set once the buyer has reached them.
const STEPS: { stage: FunnelStage; label: string; hint: string }[] = [
  { stage: 'teaser', label: 'Teaser', hint: 'Anonymized overview' },
  { stage: 'nda_signed', label: 'NDA signed', hint: 'Confidentiality in place' },
  { stage: 'cim', label: 'CIM', hint: 'Full information memorandum' },
  { stage: 'loi_submitted', label: 'LOI submitted', hint: 'Letter of intent received' },
  { stage: 'due_diligence', label: 'Due diligence', hint: 'All files open' },
  { stage: 'exclusivity_expires', label: 'Exclusivity', hint: 'Exclusive negotiating period' },
  { stage: 'closing', label: 'Closing', hint: 'Final step' },
];

/** Exclusivity / closing are date-only values: show the calendar date as-is
 * (parsing them as UTC showed the day before in US time zones). */
function formatDate(value: string | null): string | null {
  return value ? formatDateOnly(value, '') || null : null;
}

export function FunnelStepper({ state }: { state: RoomFunnelResponse }) {
  const currentIndex = STEPS.findIndex((s) => s.stage === state.stage);
  const dateFor = (stage: FunnelStage): string | null =>
    stage === 'exclusivity_expires'
      ? formatDate(state.exclusivityExpiresAt)
      : stage === 'closing'
        ? formatDate(state.closingDate)
        : null;

  return (
    <ol className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-2">
      {STEPS.map((step, i) => {
        const stepState = i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'upcoming';
        // Show the admin-set date once the buyer has reached (or passed) a dated step.
        const date = stepState === 'upcoming' ? null : dateFor(step.stage);
        return (
          <li key={step.stage} className="flex flex-1 items-start gap-3 sm:flex-col sm:gap-2">
            <div className="flex items-center gap-2 sm:w-full">
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                  stepState === 'done' && 'border-primary bg-primary text-primary-foreground',
                  stepState === 'current' && 'border-2 border-primary bg-primary-soft text-primary-ink',
                  stepState === 'upcoming' && 'border-input text-muted-foreground',
                )}
              >
                {stepState === 'done' ? <Icon name="check" size={14} /> : i + 1}
              </span>
              <span
                aria-hidden="true"
                className={cn(
                  'hidden h-px flex-1 sm:block',
                  i < currentIndex ? 'bg-primary' : 'bg-border',
                  i === STEPS.length - 1 && 'sm:hidden',
                )}
              />
            </div>
            <div>
              <p
                className={cn(
                  'text-sm font-medium',
                  stepState === 'upcoming' ? 'text-muted-foreground' : 'text-foreground',
                )}
              >
                {step.label}
                {stepState === 'current' ? (
                  <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                    Current
                  </span>
                ) : null}
              </p>
              {/* The dated steps show the admin-set date; others show their hint. */}
              <p className="text-xs text-muted-foreground">{date ?? step.hint}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
